import type { BlockKindFilterValue } from '@/blocks/domain/types';
import { BLOCK_KINDS, LEGACY_BLOCK_KIND_ALIASES, OTHER_KIND_FILTER_VALUE } from '@/blocks/domain/types';
import type { BlockLifecycleStatus, BlockStatusFilter } from '@/blocks/domain/types/BlockLifecycleStatus';
import { BLOCKS_LIST_DEFAULT_SORT } from '@/blocks/domain/types/BlockLifecycleStatus';
import { escapeSieveValue } from '@/common/infrastructure/http/sieve';

export interface BuildBlocksSieveFilterArgs {
  search?: string;
  kind?: BlockKindFilterValue;
  statusFilter?: BlockStatusFilter;
}

/**
 * other = every stored kind the app does not support: not one of the known kinds and not a legacy alias
 * (project_details is edited as scope_fields, so it is supported and never lands here).
 */
const otherKindClauses = (): string[] => [...BLOCK_KINDS, ...LEGACY_BLOCK_KIND_ALIASES.keys()].map((known) => `blockType!=${known}`);

/**
 * A kind filter also matches the legacy stored kinds that are edited as it (`scope_fields` finds
 * `project_details` too), joined with Sieve's OR separator `|` (LLAMS-617).
 */
const kindClause = (kind: string): string => {
  const legacy = [...LEGACY_BLOCK_KIND_ALIASES].filter(([, canonical]) => canonical === kind).map(([stored]) => stored);
  return `blockType==${[kind, ...legacy].join('|')}`;
};

export const buildBlocksSieveFilter = ({ search, kind, statusFilter = 'all-active' }: BuildBlocksSieveFilterArgs): string => {
  const filters: string[] = [];

  if (statusFilter === 'all-active') {
    filters.push('status!=Archived');
  } else if (statusFilter !== 'all') {
    // 'all' emits no status clause. The list request must also send includeArchived=true;
    // a missing status clause alone stays active-only on the API.
    filters.push(`status==${statusFilter}`);
  }

  if (kind === OTHER_KIND_FILTER_VALUE) {
    filters.push(...otherKindClauses());
  } else if (kind) {
    filters.push(kindClause(kind));
  }

  if (search?.trim()) {
    // Sieve's `@=` operator is "case-insensitive contains" by itself; wrapping the
    // value in `*` characters makes Sieve search for the literal asterisks.
    filters.push(`name@=${escapeSieveValue(search.trim())}`);
  }

  // Sieve filter parser uses `,` as the AND-separator between expressions.
  return filters.join(',');
};

export { BLOCKS_LIST_DEFAULT_SORT };

export const toBlockLifecycleStatus = (raw: unknown): BlockLifecycleStatus | 'Unknown' => {
  if (typeof raw !== 'string') return 'Unknown';

  const normalized = raw.trim().toLowerCase();
  if (normalized === 'draft') return 'Draft';
  if (normalized === 'published') return 'Published';
  if (normalized === 'archived') return 'Archived';

  return 'Unknown';
};

/** Wire fields that can indicate archive state independently of `status`. */
export interface BlockLifecycleStatusSource {
  status?: unknown;
  isArchived?: boolean | null;
  archivedAt?: string | null;
}

/**
 * Prefer explicit archive flags from PDFAPI (`isArchived` / `archivedAt`) so the
 * editor stays read-only even if a payload omits or mis-types `status`.
 */
export const resolveBlockLifecycleStatus = (source: BlockLifecycleStatusSource): BlockLifecycleStatus | 'Unknown' => {
  if (source.isArchived === true) {
    return 'Archived';
  }

  if (typeof source.archivedAt === 'string' && source.archivedAt.trim() !== '') {
    return 'Archived';
  }

  return toBlockLifecycleStatus(source.status);
};
